import ActionCable from "actioncable";

import { type Consumer as ActionCableConsumer } from "shared/types/actionCable";

export type UpdateHandler<T> = (data: T) => void;

export interface ActionCableSubscriptionConfig<T> {
  channel: string;
  onUpdate: UpdateHandler<T>;
  options: object;
  token: string;
}

export default class ActionCableSubscription<T> {
  private readonly channel: string;

  private readonly consumer: ActionCableConsumer;

  private readonly onUpdate: UpdateHandler<T>;

  private readonly options: object;

  private subscribeCalled: boolean = false;

  constructor({
    channel,
    onUpdate,
    options = {},
    token,
  }: ActionCableSubscriptionConfig<T>) {
    const webSocketHost = `${import.meta.env.VITE_WEBSOCKETS_URL}?token=${token}`;

    this.consumer = ActionCable.createConsumer(webSocketHost);

    this.channel = channel;
    this.options = options;
    this.onUpdate = onUpdate;
  }

  connected() {}

  disconnected() {}

  rejected() {}

  subscribe() {
    if (this.subscribeCalled) {
      throw new Error(
        "This is a single-use object. Create a new one in order to attempt subscribing again."
      );
    }

    this.subscribeCalled = true;

    return new Promise<void>(
      (resolveSubscriptionPromise, rejectSubscriptionPromise) => {
        // I guess it's not an official API, but seems the only one available
        this.consumer.connection.events.error = () => {
          rejectSubscriptionPromise(
            new Error("Could not establish WebSocket connection")
          );
        };

        this.consumer.subscriptions.create(
          { channel: this.channel, global: true, ...this.options },
          {
            connected: () => {
              resolveSubscriptionPromise();
              this.connected();
            },
            disconnected: this.disconnected,
            received: this.onUpdate,
            rejected: () => {
              rejectSubscriptionPromise(
                new Error("WebSocket connection rejected by the server")
              );

              this.rejected();
            },
          }
        );
      }
    );
  }

  unsubscribe() {
    this.consumer.subscriptions.subscriptions.forEach((subscription) =>
      this.consumer.subscriptions.remove(subscription)
    );

    this.consumer.disconnect();
  }
}
